import {
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Brush,
  Code2,
  Compass,
  Film,
  Globe2,
  Lightbulb,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Rocket,
  Search,
  Send,
  Share2,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Zap,
  Sliders,
  Tv2,
  Gamepad2,
  Building2,
  ShoppingBag,
  Stethoscope,
  ShieldCheck,
  CheckCircle2,
  Check,
  Loader2,
} from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { useCountUp, useInView, useReveal } from "@/lib/use-reveal";
import { HeroOrb } from "./SiteNav";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

/* ---------------- HERO ---------------- */

export function Hero() {
  return (
    <section className="relative overflow-hidden pt-28 pb-14 md:pt-36 md:pb-20">
      <div className="absolute inset-0 grid-bg" aria-hidden />
      <div className="glow-orb -left-32 top-10 h-[420px] w-[420px] bg-[var(--glow-purple)]" />
      <div className="glow-orb right-[-120px] top-40 h-[360px] w-[360px] bg-[var(--glow-orange)]" />
      <div className="container-x relative">
        <div className="grid items-center gap-8 lg:gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7 reveal in-view">
            <div className="inline-flex items-center gap-2 glass rounded-full px-3.5 py-1 text-[11px] uppercase tracking-[0.2em] text-muted-foreground border border-white/10">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--glow-orange)] shadow-[0_0_8px_var(--glow-orange)] animate-pulse" />
              Global Media Buying & Performance
            </div>
            <h1 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl xl:text-[2.75rem] font-bold leading-[1.18] tracking-tight max-w-xl text-white">
              Global Media Buying & Performance Marketing Agency For <span className="text-gradient">High-Growth Brands.</span>
            </h1>
            <p className="mt-3.5 max-w-lg text-xs sm:text-sm text-muted-foreground leading-relaxed">
              We plan, buy, and optimize media across Digital, TV, OOH, and social platforms—scaling brands across <strong className="text-white font-medium">India, UAE, Europe, Bangladesh, Nepal & Sri Lanka</strong>.
            </p>
            <div className="mt-3.5 p-3 rounded-xl glass-strong border border-white/10 text-xs text-foreground/80 max-w-lg flex items-center gap-2.5">
              <Sparkles className="h-3.5 w-3.5 text-[var(--glow-orange)] shrink-0" />
              <span>From <strong>iGaming to FMCG, Healthcare & Education</strong>, we turn media spend into growth.</span>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <a href="/contact-us" className="btn-hero !py-2.5 !px-5 !text-xs sm:!text-sm whitespace-nowrap">
                Book Media Strategy <ArrowRight className="h-3.5 w-3.5 shrink-0" />
              </a>
              <a href="/contact-us" className="btn-ghost !py-2.5 !px-5 !text-xs sm:!text-sm whitespace-nowrap">
                Request Media Strategy <ArrowUpRight className="h-3.5 w-3.5 shrink-0" />
              </a>
            </div>
            <div className="mt-6 flex flex-wrap items-start gap-x-6 sm:gap-x-8 gap-y-4 border-t border-white/10 pt-4 max-w-2xl">
              {[
                { k: "6+ Markets", v: "Global Execution" },
                { k: "100%", v: "Performance-Led" },
                { k: "Omnichannel", v: "TV, Digital, Print, Radio, Outdoor, Cinema, BTL" },
              ].map((s) => (
                <div key={s.k} className="shrink-0 min-w-0">
                  <div className="font-display text-sm sm:text-base font-bold text-gradient whitespace-nowrap">{s.k}</div>
                  <div className="text-[9.5px] sm:text-[11px] uppercase tracking-wider text-muted-foreground/80 mt-1 whitespace-nowrap font-medium">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <HeroOrb />
          </div>
        </div>
      </div>
    </section>
  );
}



/* ---------------- MARQUEE ---------------- */
export function Marquee() {
  const items = [
    "Performance Marketing",
    "Brand Strategy",
    "Creative Direction",
    "Web Development",
    "Influencer Marketing",
    "Video Production",
    "SEO Engineering",
    "Social Media",
  ];
  return (
    <section aria-hidden className="relative py-10 border-y border-white/5 overflow-hidden">
      <div className="flex w-max animate-marquee gap-12 whitespace-nowrap px-6">
        {[...items, ...items].map((s, i) => (
          <div
            key={i}
            className="flex items-center gap-3 font-display text-xl md:text-2xl text-muted-foreground"
          >
            <Sparkles className="h-4 w-4 text-[var(--glow-orange)]" />
            <span>{s}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- WHY KMWA ---------------- */
export function About() {
  const ref = useReveal<HTMLDivElement>();
  return (
    <section id="why-kmwa" className="relative section-pad border-t border-white/5">
      <div className="container-x">
        <div ref={ref} className="reveal grid gap-10 lg:grid-cols-12 items-start">
          <div className="lg:col-span-5">
            <SectionLabel>Why KMWA?</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold leading-tight">
              Why Brands Choose <span className="text-gradient">KMWA</span>
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground font-medium">
              Most agencies specialize in either digital or traditional media. <br />
              <span className="text-white font-semibold">We connect both.</span>
            </p>
            <div className="mt-6 p-4 rounded-2xl glass-strong border border-[var(--glow-orange)]/30 text-xs sm:text-sm font-semibold text-white inline-flex items-center gap-2">
              <Zap className="h-4 w-4 text-[var(--glow-orange)] shrink-0" />
              <span><strong>Result:</strong> Smarter spend. Lower acquisition costs. Scalable growth.</span>
            </div>
          </div>
          <div className="lg:col-span-7 grid gap-5">
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              At <strong className="text-white">KMWAdvertising</strong>, we operate as a full-spectrum media buying engine—combining strategic planning, negotiated buying power, performance attribution, and cross-market execution under one roof.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: "Strategic Media Planning", desc: "Data-led channel mix and budget allocation tailored to business growth goals." },
                { title: "Negotiated Buying Power", desc: "Maximizing ROI across TV, digital, OOH, print, radio, and cinema platforms." },
                { title: "Performance Tracking", desc: "Full-funnel attribution focused on Leads, Installs, Conversions, Revenue, and FTDs." },
                { title: "Cross-Market Execution", desc: "Unified global campaigns spanning India, UAE, Europe, Bangladesh, Nepal & Sri Lanka." },
              ].map((x) => (
                <div key={x.title} className="glass rounded-2xl p-5 border border-white/10 hover:border-white/20 transition-colors">
                  <div className="flex items-center gap-2 font-display text-sm sm:text-base font-semibold text-white">
                    <CheckCircle2 className="h-4 w-4 text-[var(--glow-orange)] shrink-0" />
                    {x.title}
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-2 leading-relaxed">{x.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground">
      <span className="h-px w-8 bg-gradient-to-r from-[var(--glow-orange)] to-transparent" />
      {children}
    </div>
  );
}

/* ---------------- SERVICES / CORE COMPETENCIES ---------------- */
const services = [
  { 
    icon: Sliders, 
    title: "Media Buying & Planning", 
    desc: "We negotiate, place, and optimise media across TV, digital, print, radio, outdoor, and cinema platforms for maximum ROI." 
  },
  { 
    icon: TrendingUp, 
    title: "Performance Marketing", 
    desc: "Data-driven campaigns focused on Leads, Installs, Conversions, Revenue Growth, and FTDs." 
  },
  { 
    icon: Globe2, 
    title: "Digital Marketing", 
    desc: "Full-funnel digital strategies across search, social, display, and programmatic ecosystems." 
  },
  { 
    icon: Gamepad2, 
    title: "Gaming & iGaming Marketing", 
    desc: "User acquisition strategies for gaming and entertainment platforms at scale." 
  },
  { 
    icon: Search, 
    title: "Brand Strategy & Research", 
    desc: "Market insights, audience mapping, and positioning strategies that guide growth." 
  },
  { 
    icon: Tv2, 
    title: "Traditional Media Execution", 
    desc: "TV, radio, print, and outdoor campaigns integrated with digital performance tracking." 
  },
];

export function Services() {
  const ref = useReveal<HTMLDivElement>();
  return (
    <section id="services" className="relative section-pad">
      <div className="absolute inset-0 grid-bg opacity-50" aria-hidden />
      <div className="container-x relative">
        <div ref={ref} className="reveal max-w-2xl">
          <SectionLabel>Our Core Competencies</SectionLabel>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold">
            Full-Spectrum <span className="text-gradient">Media & Marketing Engine.</span>
          </h2>
          <p className="mt-4 text-muted-foreground">
            From digital performance to global traditional media buying—engineered to drive scalable, high-ROI business growth.
          </p>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <ServiceCard key={s.title} {...s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({
  icon: Icon,
  title,
  desc,
  i,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
  i: number;
}) {
  return (
    <div
      className="tilt-card glass rounded-3xl p-6 relative overflow-hidden reveal"
      style={{ animationDelay: `${i * 60}ms` }}
      ref={useReveal<HTMLDivElement>()}
    >
      <div
        className="absolute -top-16 -right-16 h-40 w-40 rounded-full blur-3xl opacity-30"
        style={{
          background:
            i % 3 === 0
              ? "var(--glow-orange)"
              : i % 3 === 1
                ? "var(--glow-purple)"
                : "var(--glow-cyan)",
        }}
      />
      <div className="relative">
        <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl glass-strong">
          <Icon className="h-5 w-5 text-[var(--glow-orange)]" />
        </div>
        <h3 className="mt-5 font-display text-lg font-semibold">{title}</h3>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{desc}</p>
        <div className="mt-5 inline-flex items-center gap-1 text-xs font-medium text-foreground/80">
          Learn more <ArrowRight className="h-3.5 w-3.5" />
        </div>
      </div>
    </div>
  );
}

/* ---------------- INDUSTRY EXPERTISE ---------------- */
const projects = [
  {
    name: "Gaming & iGaming",
    tag: "User Acquisition · Scale",
    metric: "High-Velocity FTDs",
    desc: "Targeted player acquisition, app install funnels, and gaming ecosystem scale.",
    icon: Gamepad2,
    gradient: "linear-gradient(135deg, oklch(0.72 0.19 50), oklch(0.55 0.2 30))",
    shadow: "rgba(249, 115, 22, 0.4)",
  },
  {
    name: "FMCG & Consumer Brands",
    tag: "Mass Market · High Reach",
    metric: "Omnichannel Scale",
    desc: "High-impact media buying across TV, digital, print, and retail networks.",
    icon: ShoppingBag,
    gradient: "linear-gradient(135deg, oklch(0.62 0.22 300), oklch(0.55 0.2 340))",
    shadow: "rgba(168, 85, 247, 0.4)",
  },
  {
    name: "Hospitality & Travel",
    tag: "Direct Bookings · Reach",
    metric: "High ROAS Flights",
    desc: "Performance ad campaigns driving direct bookings and footfall growth.",
    icon: MapPin,
    gradient: "linear-gradient(135deg, oklch(0.78 0.14 220), oklch(0.45 0.15 250))",
    shadow: "rgba(6, 182, 212, 0.4)",
  },
  {
    name: "Real Estate",
    tag: "High-Intent Leads",
    metric: "Verified Pipeline",
    desc: "Geo-targeted lead acquisition and buyer engagement media systems.",
    icon: Building2,
    gradient: "linear-gradient(135deg, oklch(0.72 0.19 50), oklch(0.62 0.22 300))",
    shadow: "rgba(234, 179, 8, 0.4)",
  },
  {
    name: "Healthcare & Pharma",
    tag: "Compliant Media",
    metric: "Brand Trust",
    desc: "Precision medical practitioner and patient awareness strategies.",
    icon: Stethoscope,
    gradient: "linear-gradient(135deg, oklch(0.48 0.18 245), oklch(0.35 0.15 275))",
    shadow: "rgba(37, 99, 235, 0.4)",
  },
  {
    name: "E-Commerce & D2C",
    tag: "D2C Sales · Conversion",
    metric: "ROAS Scaling",
    desc: "Full-funnel digital campaigns engineered for maximum purchase velocity.",
    icon: Zap,
    gradient: "linear-gradient(135deg, oklch(0.65 0.2 140), oklch(0.5 0.18 160))",
    shadow: "rgba(16, 185, 129, 0.4)",
  },
];

export function BrandMarquee() {
  const regions = [
    { name: "UAE", category: "Middle East Media", icon: Globe2 },
    { name: "India", category: "Pan-India Reach", icon: Target },
    { name: "Europe", category: "Cross-Border Ads", icon: Zap },
    { name: "Bangladesh", category: "South Asia Flight", icon: TrendingUp },
    { name: "Nepal", category: "Regional Buying", icon: BarChart3 },
    { name: "Sri Lanka", category: "Island Performance", icon: Rocket },
  ];

  return (
    <section aria-hidden className="relative py-8 bg-black/50 border-b border-white/5 overflow-hidden">
      <div className="container-x flex flex-col gap-5">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--glow-orange)] font-semibold">
              <span className="h-2 w-2 rounded-full bg-[var(--glow-orange)] animate-pulse" />
              Global Media Footprint
            </div>
            <h3 className="text-sm sm:text-base font-display font-medium text-white/90 mt-1">
              Trusted by global brands in gaming, entertainment, and performance marketing ecosystems.
            </h3>
          </div>
        </div>

        <div className="overflow-hidden relative w-full pt-1">
          {/* Faded edges */}
          <div className="absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
          
          <div className="flex w-max animate-marquee gap-5 whitespace-nowrap">
            {[...regions, ...regions, ...regions].map((b, i) => {
              const Icon = b.icon;
              return (
                <div
                  key={i}
                  className="h-14 px-6 py-2.5 bg-white/[0.03] border border-white/10 rounded-2xl flex items-center gap-3 hover:bg-white/[0.08] hover:border-white/20 hover:scale-105 hover:shadow-[0_0_30px_rgba(255,140,0,0.15)] transition-all duration-300 group select-none cursor-default"
                >
                  <div className="h-8 w-8 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-orange)] group-hover:scale-110 group-hover:bg-[var(--glow-orange)]/10 transition-all duration-300">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex flex-col text-left">
                    <span className="text-xs font-display font-bold text-white tracking-wider">
                      {b.name}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {b.category}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export function Work() {
  const containerRef = useRef<HTMLDivElement>(null);
  const cylinderRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!containerRef.current || !cylinderRef.current) return;

    const mm = ScrollTrigger.matchMedia({
      // Desktop / Tablet only: Pin and rotate 3D cylinder
      "(min-width: 640px)": function () {
        const anim = gsap.fromTo(
          cylinderRef.current,
          { rotationY: 0 },
          {
            rotationY: -360,
            ease: "none",
            scrollTrigger: {
              trigger: containerRef.current,
              start: "top top",
              end: "+=2000",
              pin: true,
              scrub: 0.3,
              onUpdate: (self) => {
                const idx = Math.min(5, Math.max(0, Math.round(self.progress * 5)));
                setActiveIndex(idx);
              },
            },
          }
        );

        return () => {
          anim.scrollTrigger?.kill();
          anim.kill();
        };
      },
    });

    return () => {
      mm.kill();
    };
  }, []);

  return (
    <section 
      ref={containerRef}
      id="work" 
      className="relative min-h-screen sm:h-screen bg-black overflow-hidden flex flex-col justify-between border-t border-white/5 pt-20 sm:pt-24 md:pt-28 pb-10 sm:pb-6 md:pb-8"
    >
      {/* Background visual styling */}
      <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[var(--glow-orange)] blur-[150px] opacity-[0.04] pointer-events-none" />

      <div className="container-x w-full relative z-10 flex flex-col flex-1">
        
        {/* Section Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 md:gap-6 max-w-5xl mx-auto w-full select-none shrink-0 mb-6 md:mb-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2 md:mb-3">
              <span className="h-px w-8 bg-gradient-to-r from-[var(--glow-orange)] to-transparent" />
              Industry Expertise
            </div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
              Industries We <span className="text-gradient">Scale.</span>
            </h2>
            <p className="mt-1.5 md:mt-2 text-xs sm:text-sm text-muted-foreground">
              We don’t do “one-size-fits-all marketing.” We build vertical-specific media systems.
            </p>
          </div>
          <a href="/contact-us" className="btn-ghost !py-2 !px-4 md:!py-2.5 md:!px-5 !text-xs md:!text-sm">
            Request Media Strategy <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>

        {/* Mobile View: Immaculate Vertical Card Grid (sm:hidden) */}
        <div className="grid sm:hidden grid-cols-1 gap-4 py-2 w-full max-w-md mx-auto">
          {projects.map((p, idx) => {
            const IconComponent = p.icon;
            return (
              <div 
                key={p.name}
                className="rounded-3xl border border-white/10 bg-[#08080d] p-5 flex flex-col justify-between relative overflow-hidden shadow-xl"
                style={{
                  borderLeft: `3px solid ${p.shadow.includes("249") ? "var(--glow-orange)" : p.shadow.includes("168") ? "var(--glow-purple)" : "var(--glow-cyan)"}`,
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <IconComponent className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground bg-white/5 px-2.5 py-1 rounded-md border border-white/5">
                    {p.tag}
                  </span>
                </div>
                <h3 className="font-display text-lg font-bold text-white mt-3">
                  {p.name}
                </h3>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  {p.desc}
                </p>
                <div className="flex items-center justify-between border-t border-white/5 pt-3 mt-3">
                  <span className="text-xs font-semibold text-gradient" style={{ backgroundImage: p.gradient }}>
                    {p.metric}
                  </span>
                  <a href="/contact-us" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold text-white/80">
                    Scale Vertical <ArrowUpRight className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop View: Interactive 3D Cylinder Arena (hidden sm:flex) */}
        <div className="hidden sm:flex flex-1 w-full items-center justify-center relative overflow-visible">
          
          {/* Cylinder Wrapper */}
          <div className="perspective-3d w-full h-[340px] sm:h-[380px] md:h-[420px] lg:h-[450px] flex items-center justify-center relative overflow-visible">
            <div 
              ref={cylinderRef} 
              className="relative w-[180px] h-[240px] min-[480px]:w-[210px] min-[480px]:h-[270px] md:w-[250px] md:h-[310px] lg:w-[280px] lg:h-[340px] preserve-3d"
            >
              {projects.map((p, idx) => {
                const IconComponent = p.icon;
                const angle = (idx * 360) / projects.length;
                return (
                  <div 
                    key={p.name}
                    className={`cylinder-card rounded-[2rem] border border-white/10 bg-[#06060a]/95 flex flex-col justify-between p-6 select-none group overflow-hidden cursor-pointer ${
                      idx === activeIndex ? "card-active" : ""
                    }`}
                    style={{
                      "--ry": `${angle}deg`,
                      "--ty": `0px`,
                      "--shadow-color": p.shadow,
                      boxShadow: "0 10px 30px rgba(0,0,0,0.6)",
                    } as React.CSSProperties}
                  >
                    {/* Subtle card glow overlay */}
                    <div 
                      className="absolute -top-24 -right-24 h-48 w-48 rounded-full blur-[60px] opacity-10 group-hover:opacity-35 transition-opacity duration-700" 
                      style={{ background: p.shadow }}
                    />

                    {/* Top: Glowing Solution Icon Frame */}
                    <div className="h-28 md:h-32 w-full rounded-2xl bg-white/[0.02] border border-white/5 flex flex-col items-center justify-center p-6 relative overflow-hidden group-hover:border-white/15 transition-colors">
                      <div 
                        className="absolute inset-0 opacity-15 blur-xl group-hover:opacity-30 transition-opacity duration-500 pointer-events-none"
                        style={{ background: p.gradient }}
                      />
                      <div className="h-12 w-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center relative z-10 group-hover:scale-110 transition-transform duration-500 shadow-inner">
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                    </div>

                    {/* Middle: Content Metadata */}
                    <div className="my-3">
                      <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">
                        {p.tag}
                      </span>
                      <h3 className="font-display text-lg md:text-xl font-bold text-white mt-1 group-hover:text-gradient transition-all duration-300">
                        {p.name}
                      </h3>
                      <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
                        {p.desc}
                      </p>
                    </div>

                    {/* Bottom: Performance Metric Badge */}
                    <div className="flex items-center justify-between border-t border-white/5 pt-3 shrink-0">
                      <div className="text-[11px] font-display font-semibold tracking-wider text-gradient" style={{ backgroundImage: p.gradient }}>
                        {p.metric}
                      </div>
                      <div className="h-8 w-8 rounded-full bg-white/5 border border-white/5 flex items-center justify-center group-hover:bg-white/10 group-hover:border-white/20 transition-all duration-300">
                        <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-white transition-colors" />
                      </div>
                    </div>

                  </div>
                );
              })}
            </div>
          </div>

          {/* Vertical Cylinder Progress Dots on the Right */}
          <div className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 flex flex-col gap-2.5 z-20 select-none">
            {projects.map((p, idx) => (
              <div 
                key={idx}
                className={`progress-dot ${idx === activeIndex ? "dot-active" : ""}`}
              />
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}

/* ---------------- PROCESS ---------------- */
const steps = [
  { icon: Compass, title: "Discover", desc: "Audit, research and brand truth." },
  { icon: Lightbulb, title: "Strategy", desc: "Positioning, channels & KPIs." },
  { icon: Brush, title: "Create", desc: "Identity, content and campaigns." },
  { icon: Rocket, title: "Launch", desc: "Go-to-market with precision." },
  { icon: BarChart3, title: "Optimize", desc: "Test, learn, compound." },
  { icon: TrendingUp, title: "Scale", desc: "Spend smarter, grow bigger." },
];

export function Process() {
  const ref = useReveal<HTMLDivElement>();
  return (
    <section id="process" className="relative section-pad">
      <div className="glow-orb left-1/2 top-10 h-[400px] w-[400px] -translate-x-1/2 bg-[var(--glow-purple)]" />
      <div className="container-x relative">
        <div ref={ref} className="reveal max-w-2xl">
          <SectionLabel>How we work</SectionLabel>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold">
            A six-step <span className="text-gradient">growth system.</span>
          </h2>
        </div>

        <div className="mt-14 relative">
          {/* timeline line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[var(--glow-purple)] to-transparent md:-translate-x-px" />
          <ol className="space-y-10 md:space-y-16">
            {steps.map((s, i) => (
              <StepItem key={s.title} step={i + 1} {...s} flip={i % 2 === 1} />
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function StepItem({
  step,
  icon: Icon,
  title,
  desc,
  flip,
}: {
  step: number;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
  flip: boolean;
}) {
  const ref = useReveal<HTMLLIElement>();
  return (
    <li ref={ref} className="reveal relative md:grid md:grid-cols-2 md:gap-12 items-center">
      <div className={`pl-16 md:pl-0 ${flip ? "md:order-2 md:pl-12" : "md:pr-12 md:text-right"}`}>
        <div className="text-xs uppercase tracking-[0.3em] text-[var(--glow-orange)]">
          Step {String(step).padStart(2, "0")}
        </div>
        <h3 className="mt-2 font-display text-2xl md:text-3xl font-semibold">{title}</h3>
        <p className="mt-2 text-muted-foreground max-w-md md:max-w-none md:inline-block">{desc}</p>
      </div>
      <div className={`hidden md:block ${flip ? "md:order-1" : ""}`} />
      {/* node */}
      <span className="absolute left-6 md:left-1/2 top-0 -translate-x-1/2 inline-flex h-12 w-12 items-center justify-center rounded-2xl glass-strong ring-glow">
        <Icon className="h-5 w-5 text-[var(--glow-orange)]" />
      </span>
    </li>
  );
}

/* ---------------- RESULTS ---------------- */
const stats = [
  { v: 540, suffix: "+", label: "Campaigns Managed" },
  { v: 120, suffix: "+", label: "Brands Built" },
  { v: 8400, suffix: "+", label: "Creatives Delivered" },
  { v: 250000, suffix: "+", label: "Leads Generated" },
  { v: 312, suffix: "%", label: "Avg. Growth Achieved" },
];

export function Results() {
  const { ref, inView } = useInView<HTMLDivElement>(0.25);
  return (
    <section id="results" className="relative section-pad">
      <div className="container-x">
        <div className="max-w-2xl">
          <SectionLabel>Numbers that matter</SectionLabel>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold">
            Results we're <span className="text-gradient">proud of.</span>
          </h2>
        </div>
        <div
          ref={ref}
          className="mt-12 grid gap-4 grid-cols-2 lg:grid-cols-5"
        >
          {stats.map((s) => (
            <StatCard key={s.label} {...s} start={inView} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatCard({
  v,
  suffix,
  label,
  start,
}: {
  v: number;
  suffix: string;
  label: string;
  start: boolean;
}) {
  const n = useCountUp(v, 1800, start);
  const formatted =
    v >= 1000 ? n.toLocaleString() : String(n);
  return (
    <div className="glass rounded-3xl p-6 relative overflow-hidden">
      <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[var(--glow-purple)] blur-3xl opacity-25" />
      <div className="font-display text-3xl md:text-4xl font-semibold">
        <span className="text-gradient">
          {formatted}
          {suffix}
        </span>
      </div>
      <div className="mt-2 text-xs uppercase tracking-[0.25em] text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

/* ---------------- CROSSED RIBBONS ---------------- */
export function CrossedRibbons() {
  const itemsA = [
    "DESIGN",
    "DEVELOPMENT",
    "UX & UI DESIGN",
    "FRAMER",
    "PRODUCT DESIGN",
    "WEB DESIGN",
    "BRANDING",
    "CREATIVE"
  ];

  const itemsB = [
    "PERFORMANCE",
    "SEO ENGINEERING",
    "CAMPAIGN FLIGHT",
    "PROGRAMMATIC ADS",
    "CONVERSION SECURED",
    "AUDIENCE DIAGNOSTICS",
    "OPTIMIZATION LOOP"
  ];

  return (
    <section className="relative z-30 h-[100px] sm:h-[120px] md:h-[160px] flex items-center justify-center bg-black overflow-hidden select-none py-3 md:py-6">
      {/* Background glow accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-[var(--glow-purple)] blur-[100px] opacity-20 pointer-events-none" />

      {/* Ribbon B (Rotated Upward, Underneath) */}
      <div className="absolute w-[140%] py-2.5 sm:py-3 md:py-4 bg-[var(--glow-purple)] text-black font-display font-extrabold text-xs sm:text-base md:text-2xl tracking-[0.15em] uppercase rotate-[3deg] shadow-[0_10px_35px_rgba(0,0,0,0.5)] z-10 flex overflow-hidden whitespace-nowrap border-y border-black/10">
        <div className="flex w-max animate-marquee gap-6 sm:gap-8 md:gap-12 px-4" style={{ animationDirection: "reverse" }}>
          {[...itemsB, ...itemsB, ...itemsB].map((s, i) => (
            <div key={i} className="flex items-center gap-6 sm:gap-8">
              <span>{s}</span>
              <span className="opacity-40 select-none">•</span>
            </div>
          ))}
        </div>
      </div>

      {/* Ribbon A (Rotated Downward, On Top) */}
      <div className="absolute w-[140%] py-2.5 sm:py-3 md:py-4 bg-[var(--glow-orange)] text-black font-display font-extrabold text-xs sm:text-base md:text-2xl tracking-[0.15em] uppercase rotate-[-3deg] shadow-[0_15px_40px_rgba(0,0,0,0.7)] z-20 flex overflow-hidden whitespace-nowrap border-y border-black/10">
        <div className="flex w-max animate-marquee gap-6 sm:gap-8 md:gap-12 px-4">
          {[...itemsA, ...itemsA, ...itemsA].map((s, i) => (
            <div key={i} className="flex items-center gap-6 sm:gap-8">
              <span>{s}</span>
              <span className="opacity-40 select-none">•</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ---------------- CONTACT ---------------- */
export function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    budget: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus("idle");
    setErrorMessage("");

    try {
      const res = await fetch("https://formsubmit.co/ajax/sadia@krismediaworks.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          _subject: `New KMWA Lead: ${formData.name} (${formData.company || "Direct Inquiry"})`,
          _replyto: formData.email,
          _template: "table",
          Name: formData.name,
          Email: formData.email,
          Company: formData.company || "Not specified",
          Budget: formData.budget || "Not specified",
          Message: formData.message,
        }),
      });

      if (res.ok) {
        setStatus("success");
        setFormData({ name: "", email: "", company: "", budget: "", message: "" });
      } else {
        throw new Error("Failed to send message.");
      }
    } catch (err: any) {
      console.error("Form submit error:", err);
      setStatus("error");
      setErrorMessage("Could not send directly via server. Click below to email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative z-40 bg-black section-pad overflow-hidden">
      <div className="absolute inset-0 -z-0">
        <div className="glow-orb left-10 bottom-0 h-[420px] w-[420px] bg-[var(--glow-orange)]" />
        <div className="glow-orb right-0 top-10 h-[360px] w-[360px] bg-[var(--glow-purple)]" />
      </div>
      <div className="container-x relative">
        <div className="grid gap-10 lg:grid-cols-2 items-start">
          <div>
            <SectionLabel>Contact</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-6xl font-semibold leading-[1.05]">
              Let's build your <br />
              <span className="text-gradient">next digital move.</span>
            </h2>
            <p className="mt-4 text-muted-foreground max-w-md">
              Tell us about your brand, your goals and the moment you want to create. We reply
              within one business day.
            </p>
            <div className="mt-8 grid gap-3">
              <ContactRow icon={Mail} label="sadia@krismediaworks.com" href="mailto:sadia@krismediaworks.com" />
              <ContactRow icon={MessageCircle} label="WhatsApp us" href="https://wa.me/919892398474" accent />
              <ContactRow icon={Phone} label="+91 98923 98474" href="tel:+919892398474" />
              <ContactRow icon={MapPin} label="Mumbai" />
            </div>
          </div>

          <form
            name="contact"
            method="POST"
            data-netlify="true"
            className="glass-strong rounded-3xl p-6 md:p-8 ring-glow"
            onSubmit={handleSubmit}
          >
            <input type="hidden" name="form-name" value="contact" />
            
            {status === "success" && (
              <div className="mb-6 p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm flex items-start gap-3 animate-in fade-in">
                <Check className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-white">Message sent successfully!</div>
                  <div className="text-xs text-emerald-300/80 mt-0.5">
                    Thank you for reaching out. We have received your inquiry and will reply to your email within one business day.
                  </div>
                </div>
              </div>
            )}

            {status === "error" && (
              <div className="mb-6 p-4 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm">
                <div>{errorMessage}</div>
                <a
                  href={`mailto:sadia@krismediaworks.com?subject=KMWA Project Inquiry from ${encodeURIComponent(formData.name)}&body=${encodeURIComponent(
                    `Name: ${formData.name}\nEmail: ${formData.email}\nCompany: ${formData.company}\nBudget: ${formData.budget}\n\nMessage:\n${formData.message}`
                  )}`}
                  className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-[var(--glow-orange)] hover:underline"
                >
                  Send via your default email client <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
            )}

            <div className="grid gap-4">
              <div>
                <label htmlFor="name" className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
                  Your name <span className="text-[var(--glow-orange)]">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. Rahul Sharma"
                  className="mt-2 w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[var(--glow-orange)]/60 transition"
                />
              </div>

              <div>
                <label htmlFor="email" className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
                  Email <span className="text-[var(--glow-orange)]">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@company.com"
                  className="mt-2 w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[var(--glow-orange)]/60 transition"
                />
              </div>

              <div>
                <label htmlFor="company" className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
                  Company
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Brand / Company name"
                  className="mt-2 w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[var(--glow-orange)]/60 transition"
                />
              </div>

              <div>
                <label htmlFor="budget" className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
                  Project budget
                </label>
                <input
                  id="budget"
                  name="budget"
                  type="text"
                  value={formData.budget}
                  onChange={handleChange}
                  placeholder="e.g. ₹2–5L / month"
                  className="mt-2 w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[var(--glow-orange)]/60 transition"
                />
              </div>

              <div>
                <label htmlFor="message" className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
                  Tell us about your project <span className="text-[var(--glow-orange)]">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[var(--glow-orange)]/60 transition"
                  placeholder="What are you building?"
                />
              </div>

              <button 
                type="submit" 
                disabled={loading}
                className="btn-hero mt-2 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending message...
                  </>
                ) : status === "success" ? (
                  <>
                    Sent successfully!
                    <Check className="h-4 w-4" />
                  </>
                ) : (
                  <>
                    Send message
                    <Send className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function ContactRow({
  icon: Icon,
  label,
  href,
  accent,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  href?: string;
  accent?: boolean;
}) {
  const Wrapper: any = href ? "a" : "div";
  return (
    <Wrapper
      href={href}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      className={`group flex items-center gap-3 glass rounded-2xl px-4 py-3 transition hover:border-[var(--glow-orange)]/40 ${
        accent ? "ring-1 ring-[var(--glow-orange)]/30" : ""
      }`}
    >
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl glass-strong">
        <Icon className="h-4 w-4 text-[var(--glow-orange)]" />
      </span>
      <span className="text-sm">{label}</span>
      {href && (
        <ArrowUpRight className="ml-auto h-4 w-4 text-muted-foreground group-hover:text-foreground transition" />
      )}
    </Wrapper>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
  placeholder,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <label htmlFor={name} className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        className="mt-2 w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[var(--glow-orange)]/60 transition"
      />
    </div>
  );
}

/* ---------------- FOOTER ---------------- */
export function Footer() {
  return (
    <footer className="relative z-10 w-full bg-[#050508] py-12 md:py-16 border-t border-white/5">
      <div className="container-x flex flex-col md:flex-row items-center justify-between gap-6 text-sm text-muted-foreground">
        <div className="flex flex-col gap-2 max-w-md text-center md:text-left">
          <div className="flex items-center justify-center md:justify-start gap-2">
            <img 
              src="/images/kmwa_logo.png" 
              alt="KMWAdvertising" 
              className="h-6 w-auto object-contain brightness-90 mr-2"
            />
            <span className="font-semibold text-white">KMWAdvertising</span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed mt-1">
            KMWAdvertising is a full-service media planning, media buying, and performance marketing agency helping brands scale across Asia, UAE and Europe.
          </p>
          <div className="text-[11px] text-muted-foreground/60 mt-1">
            © {new Date().getFullYear()} KMWAdvertising. All rights reserved.
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-5 sm:gap-6 text-xs uppercase tracking-wider font-medium">
          <a href="/about-us" className="hover:text-foreground transition">About Us</a>
          <a href="/services" className="hover:text-foreground transition">Services</a>
          <a href="/work" className="hover:text-foreground transition">Industries</a>
          <a href="/process" className="hover:text-foreground transition">Process</a>
          <a href="/contact-us" className="hover:text-foreground transition">Contact</a>
        </div>
      </div>
    </footer>
  );
}
